import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, NavLink } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import Header from '../../../components/global/Header';
import LogoPoweredBy from '../../../components/global/LogoPoweredBy';
import Logo from '../../../components/global/Logo';
import WebGalleryHeaderCart from '../components/WebGalleryHeaderCart';
import { PHONE_AUTH_MODAL } from '../../../constants/modals';
import { openModal } from '../../../actions/modal-actions';
import Button from '../../../components/global/Button';
import { getWebGalleryWaldogramPath } from '../utilities/web-galleries';
import NavBar from '../../../components/global/NavBar';
import { ALBUM_TYPE } from '../../../constants/albums';
import HamburgerIcon from '../../../components/icons/HamburgerIcon';
import LogoutLink from '../../../components/auth/LogoutLink';
import getConfig from '../../../util/config';
import { AlbumFolderForWebGalleries } from '../graphql/queries/album-folder';
import { AlbumForWebGalleries } from '../graphql/queries/album';
import { albumSupportsWaldograms } from '../utilities/waldograms';
import {
  trackAuthEvent,
  trackWebGalleryEvent,
  ANALYTIC_EVENTS,
} from '../utilities/analytics';
import { MeQuery } from '../../../graphql/queries/account';
import UserAccountMenu from '../../pro/views/ProMasterView/UserAccountMenu/UserAccountMenu';
import { BASENAME } from '../../../constants/basenames';
import { isShutterbug } from '../../../util/account';
import WebGalleryAlbumFolderMeta from './WebGalleryAlbumFolderMeta';
import WebGalleryAlbumMeta from './WebGalleryAlbumMeta';
import {
  getCheckoutCart,
  getCheckoutCartTotalItems,
} from '../../../services/checkout/redux/checkoutSelectors';
import { CHECKOUT_CART_MODAL } from '../../../services/checkout/components/CheckoutCart/modal';

class WebGalleryHeader extends Component {
  static propTypes = {
    albumFolder: PropTypes.shape({
      id: PropTypes.string,
    }),
    album: PropTypes.shape({
      id: PropTypes.string,
    }),
    albumPhotoGroup: PropTypes.shape({
      id: PropTypes.string,
    }),
    logo: PropTypes.shape({
      url: PropTypes.string,
    }),
    totalCartItems: PropTypes.number,
    checkout: PropTypes.func.isRequired,
    login: PropTypes.func.isRequired,
    authenticated: PropTypes.bool,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  };

  static defaultProps = {
    selectedDownloads: [],
    selectedPrints: [],
  };

  onWaldogramNavClick = event => {
    const { authenticated, login, history, album } = this.props;
    const webGalleryUrl = getWebGalleryWaldogramPath(album);

    trackWebGalleryEvent(ANALYTIC_EVENTS.PHOTO_BROWSER_DID_TAP_POSTCARD_BUTTON);

    if (!authenticated) {
      event.preventDefault();

      login({
        introText: (
          <Fragment>
            Verify your mobile number to sign in and{' '}
            <strong>send cards to your favorite people!</strong> Waldo will text
            a verification code to the number below.
          </Fragment>
        ),
        onLogin: authType => {
          trackAuthEvent(authType);
          history.push(webGalleryUrl);
        },
      });
    }
  };

  onCounselorLogin = event => {
    if (event) {
      event.preventDefault();
    }

    const { login } = this.props;
    login({ resetStoreAfterLogin: true });
  };

  getAlbumOrAlbumFolderSubtype = () => {
    const { album, albumFolder } = this.props;

    if (!album && !albumFolder) {
      return undefined;
    }

    return album?.album_subtype || albumFolder?.album_subtype;
  };

  containsCollages = () => {
    const { album, albumFolder } = this.props;
    const hasAlbumCustomPrint = album?.collages?.total > 0;
    const hasAlbumFolderAlbumCustomPrint =
      (albumFolder?.album_custom_prints?.records ?? []).length > 0;

    return hasAlbumCustomPrint || hasAlbumFolderAlbumCustomPrint;
  };

  getCollageAlbumIdForNav = () => {
    const { album, albumFolder } = this.props;

    // Favor the AlbumCustomPrint on the Album if it exists
    // (i.e. we're in the album view)
    const hasAlbumCustomPrint = album?.collages?.total > 0;

    if (hasAlbumCustomPrint) {
      return album.id;
    }

    // Fallback to AlbumCustomPrints on the AlbumFolder level
    const [albumCustomPrintFromAlbumFolder] =
      albumFolder?.album_custom_prints?.records ?? [];

    return albumCustomPrintFromAlbumFolder?.album?.id;
  };

  canSendAccountCard = () => {
    const { album, albumFolder, albumPhotoGroup } = this.props;

    const type =
      album?.album_type ??
      albumFolder?.album_type ??
      albumPhotoGroup?.album?.album_type;

    return type === ALBUM_TYPE.SHUTTERBUG || !type;
  };

  render() {
    const {
      logo,
      checkout,
      login,
      authenticated,
      album,
      albumFolder,
      totalCartItems,
      user,
    } = this.props;

    // Hero images are just shown in FWG at the Gallery level
    // therefore, we'll only be checking the folder for it.
    const heroImage =
      albumFolder?.hero_image ||
      albumFolder?.root?.hero_image ||
      albumFolder?.account?.hero_image;

    const waldogramUrl = getWebGalleryWaldogramPath(album);
    const config = getConfig();

    return (
      <Fragment>
        {albumFolder && <WebGalleryAlbumFolderMeta albumFolder={albumFolder} />}
        {album && <WebGalleryAlbumMeta album={album} />}

        <Header className="web-gallery-header-main px-lg-5">
          <div className="site-branded-header">
            {logo && !heroImage ? (
              <Fragment>
                <img
                  src={logo.url}
                  alt=""
                  className="site-brand-user-brand-logo"
                />

                <LogoPoweredBy className="site-brand-user-waldo-logo logo-sm ml-1" />
              </Fragment>
            ) : (
              <Logo linksTo={config.marcomUrl} externalLink />
            )}

            <div className="d-flex align-items-center ml-auto">
              {authenticated ? (
                <Fragment>
                  {isShutterbug(user) ? (
                    <UserAccountMenu basename={BASENAME.SHUTTERBUG} />
                  ) : (
                    <LogoutLink
                      className="btn btn-outline-primary btn-sm ml-3 btn-logout"
                      redirectUrl={config.marcomUrl}
                    />
                  )}
                </Fragment>
              ) : (
                <Button
                  btnOutline
                  btnSize="sm"
                  onClick={() =>
                    login({
                      resetStoreAfterLogin: true,
                      onLogin: trackAuthEvent,
                    })
                  }
                  className="btn-login nav-link"
                >
                  Sign in
                </Button>
              )}
            </div>
            {(totalCartItems > 0 ||
              (albumSupportsWaldograms(album) && waldogramUrl)) && (
              <NavBar
                className="navbar-webgalleries"
                togglerIcon={<HamburgerIcon color="#0d84fe" />}
                togglerChildren={
                  totalCartItems > 0 && (
                    <span className="cart-header-icon-items">
                      {totalCartItems}
                    </span>
                  )
                }
              >
                <ul className="navbar-nav">
                  {albumSupportsWaldograms(album) && waldogramUrl && (
                    <li className="nav-item">
                      <NavLink
                        to={waldogramUrl}
                        className="nav-link nav-waldogram"
                        activeClassName="nav-link-active"
                        onClick={this.onWaldogramNavClick}
                      >
                        Send a Card
                      </NavLink>
                    </li>
                  )}

                  {totalCartItems > 0 && (
                    <li className="nav-item">
                      <WebGalleryHeaderCart
                        totalCartItems={totalCartItems}
                        className="nav-link"
                        checkout={checkout}
                        displayText="My Cart"
                      />
                    </li>
                  )}
                </ul>
              </NavBar>
            )}
          </div>
        </Header>
      </Fragment>
    );
  }
}

const withFolderData = graphql(AlbumFolderForWebGalleries, {
  options: ({ match }) => ({
    variables: {
      id: match.params.folderId,
    },
  }),
  props: ({ data: { albumFolder } }) => ({
    albumFolder,
    logo: albumFolder?.account?.logo_image,
  }),
  skip: ({ match }) => !match.params.folderId,
});

const withAlbumData = graphql(AlbumForWebGalleries, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { album } }) => ({
    album,
    logo: album?.creator?.logo_image,
  }),
  skip: ({ match }) => !match.params.albumId,
});

const withUserData = graphql(MeQuery, {
  props: ({ data } = {}) => ({
    user: data?.me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

// const mapStateToProps = ({ webGalleryCart, auth }) => ({
//   selectedDownloads: webGalleryCart.selectedDownloads,
//   selectedPrints: webGalleryCart.selectedPrints,
//   totalCartItems: totalCartItemsSelector({ webGalleryCart }),
//   authenticated: auth.authenticated,
// });

const mapStateToProps = state => ({
  cart: getCheckoutCart(state),
  totalCartItems: getCheckoutCartTotalItems(state),
  authenticated: state.auth.authenticated,
});

const mapDispatchToProps = dispatch => ({
  checkout: actionProps =>
    dispatch(openModal(CHECKOUT_CART_MODAL, actionProps)),
  login: actionProps => dispatch(openModal(PHONE_AUTH_MODAL, actionProps)),
});

export { WebGalleryHeader };

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withUserData,
  withFolderData,
  withAlbumData,
)(WebGalleryHeader);
